import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ChevronLeft, Home, Users, UserCog, BookOpen, DollarSign, Settings, Layout, ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useNavMenuItems from '../config/navMenu';

// Sections the menu does not list (or lists under another path).
const breadcrumbConfig = (t) => ({
  '/dashboard': { label: t('breadcrumbs.dashboard'), Icon: Layout },
  '/students': { label: t('breadcrumbs.students'), Icon: Users },
  '/clients': { label: t('breadcrumbs.students'), Icon: Users },
  '/teachers': { label: t('breadcrumbs.teachers'), Icon: UserCog },
  '/groups': { label: t('breadcrumbs.groups'), Icon: BookOpen },
  '/calendar': { label: t('sidebar.calendar'), Icon: Calendar },
  '/jibaya': { label: t('breadcrumbs.jibaya'), Icon: DollarSign },
  '/settings': { label: t('breadcrumbs.settings'), Icon: Settings },
  '/teacher': { label: t('breadcrumbs.myGroups'), Icon: BookOpen },
});

// The detail level, by section — only where the page really is that thing.
// Anything else shows the section alone rather than a wrong title: every
// nested page used to be called "Group details".
const detailLabel = (t, parts) => {
  const [section, , sub] = parts;
  if ((section === 'students' || section === 'clients' || section === 'student') && parts.length === 2) return t('breadcrumbs.studentDetails');
  if (section === 'groups' && parts.length === 2) return t('breadcrumbs.groupDetails');
  if (section === 'groups' && sub === 'performance') return t('groups.performance');
  if (section === 'lessons' && sub === 'attendance') return t('breadcrumbs.attendance');
  return null;
};

export default function Breadcrumb({ isInHeader = false, items = null }) {
  const t = useSmartTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const menu = useNavMenuItems('desktop');
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isRTL = typeof i18n?.dir === 'function' ? i18n.dir() === 'rtl' : ['ar', 'he'].includes(i18n?.language);
  const Sep = isRTL ? ChevronLeft : ChevronRight;
  const Back = isRTL ? ArrowRight : ArrowLeft;
  const pathname = location.pathname;

  // Generate breadcrumb path: Home › section › detail.
  const generateBreadcrumbs = () => {
    const home = { label: t('breadcrumbs.home'), path: '/dashboard', Icon: Home };
    // A page can name itself (e.g. Birthdays): Home › its items.
    if (Array.isArray(items) && items.length) {
      return [home, ...items.map((it, i) => ({ Icon: BookOpen, ...it, isActive: i === items.length - 1 }))];
    }
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 0 || (parts.length === 1 && parts[0] === 'dashboard')) {
      return [{ ...home, isActive: true }];
    }

    // /manager/teachers/:id is the staff profile: it belongs to the staff section.
    const sectionPath = parts[0] === 'manager' && parts[1] ? `/${parts[1]}` : `/${parts[0]}`;
    const config = breadcrumbConfig(t);
    const menuItem = menu.find((m) => m.path === sectionPath)
      || (sectionPath === '/clients' ? menu.find((m) => m.path === '/students') : null);
    const section = menuItem
      ? { label: t(menuItem.labelKey, menuItem.labelFallback ? { defaultValue: menuItem.labelFallback } : undefined), Icon: (config[sectionPath] || {}).Icon || BookOpen }
      : config[sectionPath];

    const crumbs = [home];
    const sectionTarget = sectionPath === '/clients' ? '/students' : sectionPath;
    if (section) crumbs.push({ ...section, path: sectionTarget });
    const detail = parts[0] === 'manager' ? null : detailLabel(t, parts);
    if (detail) {
      if (parts[0] === 'groups' && parts[2]) crumbs.push({ label: t('breadcrumbs.groupDetails'), path: `/groups/${parts[1]}`, Icon: BookOpen });
      crumbs.push({ label: detail, path: pathname, Icon: BookOpen });
    }
    crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], isActive: true };
    return crumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1 && isInHeader) {
    return null;
  }

  // Get the previous breadcrumb for back button
  const previousBreadcrumb = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;

  // If in header, use compact enterprise style
  if (isInHeader) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '14px',
          color: 'var(--color-text-secondary, #64748B)',
          overflow: 'hidden'
        }}
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {isLast ? (
                <span
                  style={{
                    fontWeight: '600',
                    color: 'var(--color-text-primary, #1E293B)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {breadcrumb.label}
                </span>
              ) : (
                <button
                  onClick={() => navigate(breadcrumb.path)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary, #64748B)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)';
                    e.currentTarget.style.color = 'var(--color-primary-dark)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary, #64748B)';
                  }}
                >
                  {breadcrumb.label}
                </button>
              )}

              {index < breadcrumbs.length - 1 && (
                <span style={{ color: 'var(--color-text-tertiary, #CBD5E1)', margin: '0 2px' }}>/</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Original style for non-header usage (with back button)
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.5rem' : '1rem',
        marginBottom: isMobile ? '0.5rem' : '1.5rem'
      }}
    >
      {/* Modern Back Button */}
      {previousBreadcrumb && (
        <button
          onClick={() => navigate(previousBreadcrumb.path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.5rem',
            color: 'var(--color-text-primary)',
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
            e.currentTarget.style.borderColor = 'var(--color-primary-dark)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-surface)';
            e.currentTarget.style.borderColor = 'var(--color-border)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
          }}
        >
          <Back size={16} />
          <span>{t('common.back')}</span>
        </button>
      )}

      {/* Breadcrumb Trail */}
      <div
        style={{
          display: isMobile ? 'none' : 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flex: 1,
          fontSize: '0.875rem',
          flexWrap: 'wrap'
        }}
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const IconComponent = breadcrumb.Icon;
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {breadcrumb.isActive ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: 'rgba(53, 87, 96, 0.08)',
                  borderRadius: '0.375rem'
                }}>
                  <IconComponent size={16} style={{ color: 'var(--color-primary-dark)' }} />
                  <span style={{ color: 'var(--color-primary-dark)', fontWeight: '600' }}>
                    {breadcrumb.label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => navigate(breadcrumb.path)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.375rem',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(53, 87, 96, 0.06)';
                    e.currentTarget.style.color = 'var(--color-primary-dark)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                >
                  <IconComponent size={16} />
                  <span>{breadcrumb.label}</span>
                </button>
              )}

              {index < breadcrumbs.length - 1 && (
                <Sep size={14} style={{ color: 'var(--color-text-tertiary)' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
